import { useEffect, useState } from "react";
import axios from "axios";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { AField, ATextArea, ImageUpload, SectionShell, SaveButton, Notice } from "./ui";

const useContentDoc = (key) => {
  const [value, setValue] = useState(null);
  useEffect(() => {
    axios.get(`${API}/content`).then(({ data }) => setValue(data[key] || {})).catch(() => setValue({}));
  }, [key]);
  return [value, setValue];
};

const useSaver = (key) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const save = async (value) => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axios.put(`${API}/admin/content/${key}`, { value }, authHeaders());
      setSuccess("Saved — the live site has been updated.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return { save, saving, error, success };
};

export const HeroSection = () => {
  const [hero, setHero] = useContentDoc("hero");
  const { save, saving, error, success } = useSaver("hero");
  if (!hero) return <p className="text-sm text-sage">Loading...</p>;
  const set = (k) => (v) => setHero((h) => ({ ...h, [k]: v?.target ? v.target.value : v }));
  return (
    <SectionShell
      title="Hero Content"
      subtitle="The headline, intro text and background image at the top of the homepage. Leave the background empty to use the built-in image."
      testid="admin-hero-section"
    >
      <Notice error={error} success={success} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(hero);
        }}
        className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5 max-w-3xl"
      >
        <AField label="Eyebrow Line" testid="hero-field-eyebrow" value={hero.eyebrow || ""} onChange={set("eyebrow")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <AField label="Headline Line 1" testid="hero-field-line1" value={hero.line1 || ""} onChange={set("line1")} />
          <AField label="Headline Line 2" testid="hero-field-line2" value={hero.line2 || ""} onChange={set("line2")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <AField label="Headline Line 3" testid="hero-field-line3pre" value={hero.line3_pre || ""} onChange={set("line3_pre")} />
          <AField label="Line 3 Gold Italic Word" testid="hero-field-line3accent" value={hero.line3_accent || ""} onChange={set("line3_accent")} />
        </div>
        <ATextArea label="Intro Paragraph" testid="hero-field-subheading" value={hero.subheading || ""} onChange={set("subheading")} rows={4} />
        <ImageUpload label="Background Image" testid="hero-bg-upload" value={hero.bg_image || ""} onChange={set("bg_image")} hint="Wide, cinematic image — at least 1920px wide recommended." />
        <SaveButton saving={saving} testid="hero-save-button" />
      </form>
    </SectionShell>
  );
};

const BANNER_PAGES = [
  { key: "about", label: "About" },
  { key: "investors", label: "Property Investors" },
  { key: "tenant", label: "Tenant & Leasehold Advice" },
  { key: "how_we_work", label: "How We Work" },
];

export const BannersSection = () => {
  const [banners, setBanners] = useContentDoc("banners");
  const { save, saving, error, success } = useSaver("banners");
  if (!banners) return <p className="text-sm text-sage">Loading...</p>;
  return (
    <SectionShell
      title="Page Banners"
      subtitle="The full-width banner image at the top of each inner page. Pages without an upload keep their built-in image."
      testid="admin-banners-section"
    >
      <Notice error={error} success={success} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(banners);
        }}
        className="space-y-6 max-w-3xl"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {BANNER_PAGES.map((p) => (
            <div key={p.key} className="bg-white border border-forest/10 p-5">
              <ImageUpload
                label={p.label}
                testid={`banner-upload-${p.key}`}
                value={banners[p.key] || ""}
                onChange={(v) => setBanners((b) => ({ ...b, [p.key]: v }))}
                hint="At least 1920px wide recommended."
              />
            </div>
          ))}
        </div>
        <SaveButton saving={saving} testid="banners-save-button" />
      </form>
    </SectionShell>
  );
};

export const ContactSection = () => {
  const [contact, setContact] = useContentDoc("contact");
  const { save, saving, error, success } = useSaver("contact");
  if (!contact) return <p className="text-sm text-sage">Loading...</p>;
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));
  return (
    <SectionShell
      title="Contact Details"
      subtitle="Shown on the Contact page and in the footer across the whole site."
      testid="admin-contact-section"
    >
      <Notice error={error} success={success} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(contact);
        }}
        className="bg-white border border-forest/10 p-6 sm:p-8 space-y-5 max-w-2xl"
      >
        <AField label="Business Email" testid="contact-field-email" type="email" value={contact.email || ""} onChange={set("email")} />
        <AField label="Phone Number" testid="contact-field-phone" value={contact.phone || ""} onChange={set("phone")} />
        <AField label="Address" testid="contact-field-address" value={contact.address || ""} onChange={set("address")} />
        <AField label="Service Area" testid="contact-field-area" value={contact.area || ""} onChange={set("area")} />
        <AField
          label="Calendly Booking URL"
          testid="contact-field-calendly"
          value={contact.calendly_url || ""}
          onChange={set("calendly_url")}
          placeholder="https://calendly.com/your-handle/20min"
          hint="Paste your Calendly event link (e.g. a 20‑minute consultation). It will appear inline on the Contact page. Leave blank to hide."
        />
        <SaveButton saving={saving} testid="contact-save-button" />
      </form>
    </SectionShell>
  );
};
